"use client";

import Link from "next/link";
import Image from "next/image";
import {
  useRouter,
  notFound,
  useSelectedLayoutSegment,
  usePathname,
} from "next/navigation";
import { Box } from "~/components/Box/Box";
import { Hidden } from "~/components/Hidden/Hidden";
import { NavigationBar } from "~/components/NavigationBar/NavigationBar";
import { useLocation } from "~/hooks/locations";
import { Page } from "~/types";
import { CartProvider, useCart } from "~/hooks/cart";
import { HideWhenEmbedded } from "~/components/HideWhenEmbedded/HideWhenEmbedded";
import { ReactElement } from "react";
import { DesktopCheckoutButton } from "~/components/DesktopCheckoutButton/DesktopCheckoutButton";
import { Inline } from "~/components/Inline/Inline";
import { Location } from "~/types";
import styles from "./Header.module.css";
import { OrderingOptionsButton } from "~/components/OrderingOptionsButton/OrderingOptionsButton";
import { useUser } from "~/hooks/user";
import Skeleton from "react-loading-skeleton";
import { useOrderingOptions } from "~/hooks/ordering-options";

export default function ClientLocationLayout(props: {
  params: { location: string };
  children: React.ReactNode;
}) {
  const locationRef = props.params.location;
  const { location, loading } = useLocation(locationRef);
  const router = useRouter();
  const segment = useSelectedLayoutSegment() as Page | null;
  const pathName = usePathname();

  const page = pathName.includes("promo-code") ? "promo-code" : segment;

  if (loading && !location) return null;
  if (!location) notFound();

  const goToCart = () =>
    router.push(`/${locationRef}/cart`, {
      scroll: false,
    });

  const goToLocation = () =>
    router.push(`/${locationRef}`, {
      scroll: false,
    });

  return (
    <>
      <CartProvider locationRef={locationRef}>
        <HideWhenEmbedded>
          <Header
            locationRef={locationRef}
            page={page}
            location={location}
            goToCart={goToCart}
            goToLocation={goToLocation}
          />
        </HideWhenEmbedded>
        {props.children}
      </CartProvider>
    </>
  );
}

function Header({
  goToLocation,
  goToCart,
  locationRef,
  location,
  page,
}: {
  locationRef: string;
  location: Location;
  goToCart: () => void;
  goToLocation: () => void;
  page: Page | null;
}) {
  const { cart } = useCart();
  const { user, isLoading: userIsLoading } = useUser({
    revalidateOnFocus: false,
  });

  const { availableDispatchOptions } = useOrderingOptions(locationRef);

  const authenticated = Boolean(user);
  return (
    <header style={{ position: "sticky", top: 0, zIndex: 2 }}>
      <Hidden below="desktop">
        <HeaderBox>
          <Box
            display={"flex"}
            width={"full"}
            flexDirection={"row"}
            alignItems="center"
            justifyContent={"spaceBetween"}
            paddingLeft="5"
          >
            <OrderingOptionsButton
              page={page}
              location={location}
              onClickDetails={() => goToLocation()}
            />
            <NavBox
              page={page}
              locationRef={locationRef}
              authenticated={authenticated}
              userIsLoading={userIsLoading}
            />
            <DesktopCheckoutButton
              itemCount={cart.items.reduce(
                (acc, item) => acc + item.quantity,
                0,
              )}
              onGoToCheckout={goToCart}
            />
          </Box>
        </HeaderBox>
      </Hidden>
      <Hidden above="tablet">
        <HeaderBox>
          <Box
            display={"flex"}
            width={"full"}
            flexDirection={"rowReverse"}
            alignItems="center"
          >
            <OrderingOptionsButton
              page={page}
              location={location}
              onClickDetails={() => goToLocation()}
            />
          </Box>
        </HeaderBox>
        <Hidden above="tablet">
          <NavigationBar
            displayMenu={Boolean(
              availableDispatchOptions && availableDispatchOptions.length,
            )}
            activeItem={page || undefined}
            locationRef={locationRef}
            authenticated={Boolean(user)}
            userIsLoading={userIsLoading}
          />
        </Hidden>
      </Hidden>
    </header>
  );
}

function NavBox({
  page,
  locationRef,
  authenticated,
  userIsLoading,
}: {
  page: Page | null;
  locationRef: string;
  authenticated: boolean;
  userIsLoading: boolean;
}) {
  return (
    <Box
      flexGrow={1}
      display={"flex"}
      paddingLeft={"2"}
      paddingRight={"4"}
      flexDirection={"rowReverse"}
    >
      <Inline space={"4"} alignY="center">
        <NavBoxLink active={page === "menu"} href={`/${locationRef}/menu`}>
          MENU
        </NavBoxLink>
        <NavBoxLink
          active={page === "loyalty"}
          href={`/${locationRef}/loyalty`}
        >
          LOYALTY
        </NavBoxLink>
        <NavBoxLink
          active={page === "promo-code"}
          href={`/${locationRef}/account/promo-code`}
        >
          PROMO CODE
        </NavBoxLink>
        {userIsLoading ? (
          <Skeleton
            style={{ opacity: 0.2 }}
            width={68}
            className={styles.headerLink}
          />
        ) : (
          <NavBoxLink
            active={["account", "activity"].includes(page || "")}
            href={
              authenticated
                ? `/${locationRef}/account`
                : "/login?next=" + encodeURIComponent(`/${locationRef}/menu`)
            }
          >
            {authenticated ? "ACCOUNT" : "LOGIN"}
          </NavBoxLink>
        )}
      </Inline>
    </Box>
  );
}

function NavBoxLink({
  href,
  active,
  children,
}: {
  href: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      className={active ? styles.headerLinkActive : styles.headerLink}
      href={href}
      prefetch={false}
    >
      {children}
    </Link>
  );
}

function HeaderBox(props: { children: ReactElement }) {
  return (
    <Box
      display={"flex"}
      className="stickyHeader" // Used by SearchCategoryBar for the sticky top and drop shadow IntersectObserver
      style={{
        height: 75,
        backgroundColor: "var(--color-jets-black)",
      }}
      alignItems={"center"}
      justifyContent="center"
      paddingX={["4", "4", "5"]}
    >
      <Box display="flex" flexGrow={1} style={{ maxWidth: 1500 }}>
        <Hidden above="mobile">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Image
              alt="Jet's Pizza"
              src="/images/jetspizzalogo_mobile.png"
              width={72}
              height={44}
            />
          </div>
        </Hidden>
        <Hidden below="tablet">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Image
              alt="Jet's Pizza"
              src="/images/jetspizzalogo_desktop.png"
              height={42}
              width={280}
              className={"largeLogo"}
            />
            <Image
              alt="Jet's Pizza"
              src="/images/jetspizzalogo_mobile.png"
              width={72}
              height={44}
              className={"smallLogo"}
            />
          </div>
        </Hidden>
        <div style={{ flex: 1, justifyContent: "center" }}>
          {props.children}
        </div>
      </Box>
    </Box>
  );
}
